/** Something is in flight.
 *
 * Deliberately quiet: an arc of the accent at the weight of a hairline, not a
 * filled ring. It announces itself once through a live region so that waiting
 * is audible as well as visible, and it stops turning under
 * `prefers-reduced-motion` — the stylesheet collapses the animation, so what
 * is left is a static mark rather than a jitter.
 */

export interface SpinnerProps {
  size?: number;
  /** What is being waited for: "loading sessions". */
  label?: string;
  className?: string;
}

export function Spinner({ size = 16, label, className }: SpinnerProps) {
  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: 'var(--color-neutral-500)' }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        style={{ animation: 'spin 900ms linear infinite', display: 'block' }}
      >
        <circle cx="12" cy="12" r="9" stroke="var(--color-neutral-800)" strokeWidth="2.5" />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="var(--color-accent)"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
      {label ? <span style={{ fontSize: 12 }}>{label}</span> : null}
      <span role="status" aria-live="polite" className="sr-only">
        {label ?? 'loading'}
      </span>
    </span>
  );
}

/** The whole-panel version, for a screen that has nothing to show yet. */
export function LoadingPanel({ label = 'loading' }: { label?: string }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', padding: '64px 0' }}>
      <Spinner size={20} label={label} />
    </div>
  );
}

export default Spinner;
